/**
 * Welcome Location Prompt (ENH-004)
 * First-visit popup explaining same-day delivery and asking for ZIP
 */
import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MapPinIcon, TruckIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';

export default function WelcomeLocationPrompt() {
  const {
    zipCode,
    hasSeenLocationPrompt,
    markLocationPromptSeen,
    setZipCode,
    deliveryInfo,
    isLoading
  } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [inputZip, setInputZip] = useState('');
  const [step, setStep] = useState<'welcome' | 'input' | 'success'>('welcome');

  // Show prompt after a short delay if user hasn't set location
  useEffect(() => {
    if (!hasSeenLocationPrompt && !zipCode) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000); // 2 second delay after page load

      return () => clearTimeout(timer);
    }
  }, [hasSeenLocationPrompt, zipCode]);

  const handleClose = () => {
    markLocationPromptSeen();
    setIsOpen(false);
  };

  const handleSkip = () => {
    markLocationPromptSeen();
    setIsOpen(false);
  };

  const handleContinueToInput = () => {
    setStep('input');
  };

  const handleSubmitZip = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputZip.length >= 5) {
      try {
        await setZipCode(inputZip);
        setStep('success');
      } catch (err) {
        // Error is shown via context
      }
    }
  };

  const handleFinish = () => {
    markLocationPromptSeen();
    setIsOpen(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-[calc(100%-32px)] sm:w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {step === 'welcome' && (
                  <>
                    {/* Hero Section */}
                    <div className="relative bg-black px-6 py-10 text-center overflow-hidden">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                          backgroundSize: '20px 20px'
                        }} />
                      </div>

                      <div className="relative">
                        <div className="mx-auto w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mb-6">
                          <SparklesIcon className="h-10 w-10 text-white" />
                        </div>
                        <Dialog.Title className="text-3xl font-heading text-white tracking-wide">
                          WELCOME TO ARKX
                        </Dialog.Title>
                        <p className="mt-3 text-white/80 text-lg">
                          Same-Day Delivery in Los Angeles
                        </p>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="px-6 py-6 space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <TruckIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Same-Day Delivery</h3>
                          <p className="text-sm text-gray-600">Order by 6:30 PM, get it today</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <ClockIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Fast Delivery</h3>
                          <p className="text-sm text-gray-600">Typically 30-60 minutes</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <MapPinIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Local Brands</h3>
                          <p className="text-sm text-gray-600">Curated LA fashion & lifestyle</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-6 pb-6 space-y-3">
                      <button
                        onClick={handleContinueToInput}
                        className="w-full py-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
                      >
                        Check My Delivery Area
                      </button>
                      <button
                        onClick={handleSkip}
                        className="w-full py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                      >
                        Maybe later
                      </button>
                    </div>
                  </>
                )}

                {step === 'input' && (
                  <>
                    <div className="relative bg-black px-6 py-8 text-center">
                      <div className="mx-auto w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
                        <MapPinIcon className="h-8 w-8 text-white" />
                      </div>
                      <Dialog.Title className="text-2xl font-heading text-white tracking-wide">
                        ENTER YOUR ZIP CODE
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-white/70">
                        We'll show you delivery options for your area
                      </p>
                    </div>

                    <div className="px-6 py-6">
                      <form onSubmit={handleSubmitZip}>
                        <input
                          type="text"
                          value={inputZip}
                          onChange={(e) => setInputZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                          placeholder="Enter ZIP code"
                          className="w-full px-4 py-4 text-lg text-center border-2 border-gray-200 rounded-xl focus:border-black focus:ring-0 font-mono tracking-widest transition-colors"
                          maxLength={5}
                          autoFocus
                        />

                        <button
                          type="submit"
                          disabled={inputZip.length < 5 || isLoading}
                          className="w-full mt-4 py-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                              Checking...
                            </span>
                          ) : (
                            'Check Availability'
                          )}
                        </button>
                      </form>

                      <button
                        onClick={handleSkip}
                        className="w-full mt-4 py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                      >
                        Skip for now
                      </button>
                    </div>
                  </>
                )}

                {step === 'success' && (
                  <>
                    <div className="relative bg-black px-6 py-8 text-center">
                      <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                        <TruckIcon className="h-8 w-8 text-green-400" />
                      </div>
                      <Dialog.Title className="text-2xl font-heading text-white tracking-wide">
                        {deliveryInfo?.is_eligible ? "YOU'RE IN!" : "NOT YET IN YOUR AREA"}
                      </Dialog.Title>
                    </div>

                    <div className="px-6 py-6">
                      {deliveryInfo?.is_eligible ? (
                        <>
                          <div className="text-center mb-6">
                            <p className="text-lg font-semibold text-gray-900">
                              Great news! We deliver to {deliveryInfo.zip_code}
                            </p>
                            <p className="text-gray-600 mt-1">
                              {deliveryInfo.delivery_window?.display_text}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-black">
                                {deliveryInfo.eta_display}
                              </p>
                              <p className="text-xs text-gray-500">Delivery Time</p>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-black">
                                ${deliveryInfo.shipping_cost}
                              </p>
                              <p className="text-xs text-gray-500">Delivery Fee</p>
                            </div>
                          </div>

                          <button
                            onClick={handleFinish}
                            className="w-full py-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
                          >
                            Start Shopping
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="text-center mb-6">
                            <p className="text-lg font-semibold text-gray-900">
                              We're not in {inputZip} yet
                            </p>
                            <p className="text-gray-600 mt-1">
                              But we're expanding! You can still browse our collection.
                            </p>
                          </div>

                          <button
                            onClick={handleFinish}
                            className="w-full py-4 bg-black text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
                          >
                            Browse Anyway
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
